import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { useDiscardOwnIntakeDraftMutation } from '@/hooks/useIntake';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface IntakeDraftDiscardButtonProps {
  submissionId: string;
  petName: string;
}

export function IntakeDraftDiscardButton({ submissionId, petName }: IntakeDraftDiscardButtonProps) {
  const { t } = useTranslation('intake');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const discardMutation = useDiscardOwnIntakeDraftMutation();

  function discardDraft() {
    discardMutation.mutate(submissionId, {
      onSuccess: function closeAndConfirm() {
        setIsConfirmationOpen(false);
        showSuccessNotification(t('portalHome.discarded'));
      },
      onError: function reportFailure(error) {
        const message = resolveApiErrorMessage(error);
        showErrorNotification(message.length > 0 ? message : t('portalHome.discardError'));
      },
    });
  }

  return (
    <>
      <button
        type="button"
        aria-label={t('portalHome.discardAriaLabel', { petName })}
        title={t('portalHome.discard')}
        onClick={function openConfirmation() {
          setIsConfirmationOpen(true);
        }}
        className="shrink-0 min-w-[44px] min-h-[44px] rounded-xl flex items-center justify-center text-zoomuted hover:text-zooerrortext hover:bg-zooerrorbg transition-colors"
      >
        <Icon name="Trash" />
      </button>
      {isConfirmationOpen === false ? null : (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('portalHome.discardConfirmTitle')}
          description={t('portalHome.discardConfirmBody')}
          confirmLabel={t('portalHome.discardConfirm')}
          isConfirming={discardMutation.isPending}
          onConfirm={discardDraft}
          onCancel={function closeConfirmation() {
            setIsConfirmationOpen(false);
          }}
        />
      )}
    </>
  );
}
